import { ReactNode } from 'react';
import logo from '../../assets/logo.svg';
import { BrandBackdrop } from '../../components/BrandBackdrop';

export type OnboardingStep =
  | 'type'
  | 'setup'
  | 'personal-setup'
  | 'outcomes'
  | 'pricing'
  | 'review'
  | 'invites'
  | 'complete';

interface OnboardingLayoutProps {
  currentStep: OnboardingStep;
  children: ReactNode;
  workspaceType?: 'personal' | 'team' | 'enterprise' | null;
  onboardingMode?: 'business' | 'personal' | null;
  wide?: boolean;
}

/** Heroicons-style outline paths, one per step (purely decorative). */
const STEP_ICON: Partial<Record<OnboardingStep, string>> = {
  setup: 'M2.25 21h19.5M3.75 21V6.75l7.5-3 7.5 3V21M9 9h1.5m3 0H15m-6 3h1.5m3 0H15m-6 3h1.5m3 0H15',
  'personal-setup': 'M2.25 12l8.954-8.955a1.126 1.126 0 011.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75',
  outcomes: 'M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z',
  pricing: 'M9.568 3H5.25A2.25 2.25 0 003 5.25v4.318c0 .597.237 1.17.659 1.591l9.581 9.581a2.25 2.25 0 003.182 0l4.318-4.318a2.25 2.25 0 000-3.182L11.16 3.66A2.25 2.25 0 009.568 3zM6 6h.008v.008H6V6z',
  review: 'M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
  invites: 'M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198v.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0z',
  complete: 'M3 3v1.5M3 21v-6m0 0l2.77-.693a9 9 0 016.208.682l.108.054a9 9 0 006.086.71l3.114-.732a48.524 48.524 0 01-.005-10.499l-3.11.732a9 9 0 01-6.085-.711l-.108-.054a9 9 0 00-6.208-.682L3 4.5M3 15V4.5',
};

const businessSteps: { key: OnboardingStep; label: string }[] = [
  { key: 'setup', label: 'Setup' },
  { key: 'outcomes', label: 'Outcomes' },
  { key: 'pricing', label: 'Pricing' },
  { key: 'review', label: 'Review' },
  { key: 'invites', label: 'Invites' },
  { key: 'complete', label: 'Complete' },
];

const personalSteps: { key: OnboardingStep; label: string }[] = [
  { key: 'personal-setup', label: 'Setup' },
  { key: 'outcomes', label: 'Outcomes' },
  { key: 'pricing', label: 'Plan' },
  { key: 'complete', label: 'Complete' },
];

/**
 * Shared layout for onboarding flow with progress indicator.
 *
 * Personal mode shows the 4-step bar (Setup → Outcomes → Plan → Complete).
 * Business mode shows the 6-step bar (Setup → Outcomes → Pricing → Review →
 * Invites → Complete), with the invites step filtered out when
 * workspace.type === 'personal' — the long-standing rule for business-mode
 * users with a personally-typed workspace.
 *
 * When `onboardingMode` is `null` / `undefined` (caller hasn't loaded
 * onboarding state yet), the progress bar renders a neutral path-agnostic
 * **skeleton** rather than defaulting to the business 8-step layout.
 * That default previously caused a visible "8 → 3" flicker for personal
 * users whose pages mounted with `onboardingMode = null` before the
 * `/api/onboarding/state` fetch resolved.
 *
 * Business-only screens (setup, outcomes, review, invites) pass
 * `onboardingMode="business"` explicitly so they render the business bar
 * immediately, without going through the skeleton.
 */
/**
 * The premium onboarding backdrop: the LevelUp brand image under a deep
 * slate wash with a soft emerald glow, and the logo. Every onboarding screen
 * (including the "What are you creating?" chooser) renders on it, so the whole
 * journey looks like one product. Content sits in white cards on top.
 */
export function OnboardingBackdrop({ children }: { children: ReactNode }) {
  return (
    <BrandBackdrop>
      <header className="flex justify-center px-4 pt-3 pb-0 sm:pt-6">
        <img src={logo} alt="LevelUp" className="h-6 sm:h-9 drop-shadow" />
      </header>
      {children}
    </BrandBackdrop>
  );
}

export function OnboardingLayout({ currentStep, children, workspaceType, onboardingMode, wide = false }: OnboardingLayoutProps) {
  // Path-agnostic skeleton when the caller hasn't told us the mode yet.
  const modeKnown = onboardingMode === 'personal' || onboardingMode === 'business';
  const contentWidth = wide ? 'max-w-5xl' : 'max-w-2xl';

  if (!modeKnown) {
    return (
      <OnboardingBackdrop>
        {/* Skeleton progress bar — same outer shape as the real bar so the
            layout doesn't jump when the mode resolves, but no labels and
            no step count are revealed. */}
        <div className="mx-auto w-full max-w-3xl px-3 py-3 sm:px-4 sm:py-6" data-onboarding-progress="">
          <div
            role="status"
            aria-label="Loading onboarding progress"
            className="h-10 w-full rounded-full bg-white/10 animate-pulse"
          />
        </div>
        <main className="flex flex-1 items-start justify-center px-2.5 sm:px-4 pb-6 sm:pb-16 pt-0.5 sm:pt-2">
          <div className={`w-full ${contentWidth}`}>{children}</div>
        </main>
      </OnboardingBackdrop>
    );
  }

  // Personal onboarding mode: simplified step sequence
  const steps = onboardingMode === 'personal' ? personalSteps : businessSteps;

  // Business mode: personal workspaces skip invites step
  const visibleSteps = onboardingMode !== 'personal' && workspaceType === 'personal'
    ? steps.filter(s => s.key !== 'invites')
    : steps;

  const currentIndex = visibleSteps.findIndex(s => s.key === currentStep);

  return (
    <OnboardingBackdrop>
      {/* Progress */}
      <nav aria-label="Onboarding progress" data-onboarding-progress="" className="mx-auto w-full max-w-4xl px-3 py-2.5 sm:px-4 sm:py-5">
        <div className="rounded-2xl border border-white/10 bg-white/[0.04] px-2.5 py-2 sm:px-4 sm:py-3 lg:px-5 lg:py-4 shadow-2xl shadow-black/30 backdrop-blur-md">
        <ol className="flex items-center">
          {visibleSteps.map((step, index) => {
            const isActive = index === currentIndex;
            const isComplete = index < currentIndex;
            const isLast = index === visibleSteps.length - 1;
            const icon = STEP_ICON[step.key];
            return (
              <li key={step.key} className={`flex items-center ${isLast ? '' : 'flex-1'}`}>
                <div className="flex items-center gap-2.5" aria-current={isActive ? 'step' : undefined} title={step.label}>
                  <span
                    className={`relative flex h-7 w-7 sm:h-10 sm:w-10 flex-none items-center justify-center rounded-full text-xs sm:text-sm font-semibold transition-all ${
                      isComplete
                        ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-500/40'
                        : isActive
                        ? 'bg-gradient-to-br from-emerald-400 to-emerald-600 text-white shadow-lg shadow-emerald-500/50 ring-2 sm:ring-4 ring-emerald-400/25'
                        : 'bg-white/5 text-white/50 ring-1 ring-white/15'
                    }`}
                  >
                    {isComplete ? (
                      <svg className="h-3.5 w-3.5 sm:h-5 sm:w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    ) : icon ? (
                      <svg className="h-3.5 w-3.5 sm:h-5 sm:w-5" fill="none" stroke="currentColor" strokeWidth={1.6} viewBox="0 0 24 24" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" d={icon} />
                      </svg>
                    ) : (
                      index + 1
                    )}
                    {isActive && (
                      <span aria-hidden="true" className="absolute inset-0 rounded-full animate-ping bg-emerald-400/20" />
                    )}
                  </span>
                  <span className="hidden flex-col leading-tight lg:flex">
                    <span className={`text-[10px] font-semibold uppercase tracking-[0.18em] ${isActive ? 'text-emerald-300' : 'text-white/35'}`}>
                      Step {index + 1}
                    </span>
                    <span
                      data-step-label=""
                      className={`text-sm ${
                        isActive ? 'font-semibold text-white' : isComplete ? 'text-white/85' : 'text-white/50'
                      }`}
                    >
                      {step.label}
                    </span>
                  </span>
                </div>
                {!isLast && (
                  <span aria-hidden="true" className="mx-1 sm:mx-2 lg:mx-3 h-0.5 min-w-[6px] flex-1 overflow-hidden rounded-full bg-white/10">
                    <span
                      className={`block h-full rounded-full bg-gradient-to-r from-emerald-400 to-emerald-500 transition-all duration-500 ${
                        isComplete ? 'w-full' : 'w-0'
                      }`}
                    />
                  </span>
                )}
              </li>
            );
          })}
        </ol>
        {/* Below lg the step names are hidden in the row, so name the current one. */}
        <p className="mt-1.5 text-center text-[11px] sm:mt-2.5 sm:text-xs lg:hidden">
          <span className="font-semibold uppercase tracking-[0.16em] text-emerald-300">
            Step {Math.max(currentIndex, 0) + 1} of {visibleSteps.length}
          </span>
          <span className="text-white/40"> · </span>
          <span className="font-semibold text-white">{visibleSteps[Math.max(currentIndex, 0)]?.label}</span>
        </p>
        </div>
      </nav>

      <main className="flex flex-1 items-start justify-center px-2.5 sm:px-4 pb-6 sm:pb-16 pt-0.5 sm:pt-2">
        <div className={`w-full ${contentWidth}`}>{children}</div>
      </main>
    </OnboardingBackdrop>
  );
}
